import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs, { Dayjs } from 'dayjs';
import { DialogProps } from '@mui/material/Dialog';
import { capitalize } from '@mui/material/utils';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import { Qualification } from '@libs/models/talents';
import { getShiftType } from '@libs/models/shifts/shift';
import { Flex, PrimaryDialog } from '@atoms/layout';
import { LoadingButton, Button } from '@atoms/buttons';
import { Select, TextField } from '@atoms/inputs';
import { FaIcon } from '@atoms/icons';
import { Box } from '@mui/material';

const declineReasons = ['Schedule conflict', 'Sick', 'Sick child', 'Agency error', 'Employee terminated', 'Other'];

type DeclineDialogProps = Omit<DialogProps, 'onSubmit'> & {
  shift: ShiftInfo;
  onSubmit?: (reason: string) => void;
  loading?: boolean;
  warningMessage?: ReactNode;
};
export function DeclineDialog({ shift, onSubmit, loading, warningMessage, ...props }: DeclineDialogProps) {
  return (
    <PrimaryDialog fullWidth maxWidth={'xs'} {...props}>
      <Content
        shift={shift}
        onCloseClick={() => props.onClose?.({}, 'escapeKeyDown')}
        onSubmit={onSubmit}
        loading={loading}
        warningMessage={warningMessage}
      />
    </PrimaryDialog>
  );
}

type ContentProps = {
  shift: ShiftInfo;
  onCloseClick: () => void;
  onSubmit?: (reason: string) => void;
  loading?: boolean;
  warningMessage?: ReactNode;
};
function Content({ shift, onCloseClick, onSubmit, loading = false, warningMessage }: ContentProps) {
  const intl = useIntl();
  const [reason, setReason] = useState('');
  const [reasonComment, setReasonComment] = useState('');
  const validValue = reason === 'custom' ? reasonComment !== '' : reason !== '';
  const shiftType = getShiftType(dayjs(shift?.shiftStart));
  const to = dayjs(shift?.shiftStart).add(shift?.shiftLength ?? 0, 'millisecond');

  return (
    <>
      <DialogTitle sx={{ px: 3, pt: 4, pb: 2 }}>
        <Flex justifyContent="space-between">
          <Flex column gap={1}>
            <Typography variant="h5">Decline the assignment</Typography>
            <Typography variant="body1" color="text.secondary">
              <FormattedMessage id="shifts.unassignDialog.subtitle" />
            </Typography>
          </Flex>
          <Box>
            <IconButton sx={{ width: '40px', position: 'relative', top: -5, right: -5 }} onClick={onCloseClick}>
              <FaIcon name="xmark" variant="light" />
            </IconButton>
          </Box>
        </Flex>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1">
          <FormattedMessage
            id="shifts.unassignDialog.shiftDetails"
            values={{
              period: `${intl.formatTime(shift?.shiftStart?.toDate(), { hour: 'numeric' })} - ${intl.formatTime(to.toDate(), { hour: 'numeric' })}`,
              length: intl.formatMessage(
                { id: 'shifts.length' },
                {
                  length: Math.round((shift?.shiftLength ?? 0) / 1000 / 60 / 60),
                },
              ),
              type: capitalize(shiftType),
              qualifications: shift?.qualifications?.map((qual) => qual.abbreviation).join(', '),
              assignee: shift?.assigneeName,
            }}
          />
        </Typography>

        <Flex mt={2}>
          <Select
            options={declineReasons.map((r) => ({
              value: r,
              label: r,
              key: r,
            }))}
            value={reason}
            size="small"
            required
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            label={<FormattedMessage id="shifts.unassignDialog.reasonLabel" />}
            placeholder={intl.formatMessage({
              id: 'shifts.unassignDialog.reasonPlaceholder',
            })}
          />
        </Flex>
        <Collapse in={reason === 'Other'} timeout={150}>
          <Box mt={2}>
            <TextField
              value={reasonComment}
              onChange={(e) => setReasonComment(e.target.value)}
              label={<FormattedMessage id="shifts.unassignDialog.reasonCommentLabel" />}
              placeholder={intl.formatMessage({
                id: 'shifts.unassignDialog.reasonCommentPlaceholder',
              })}
              required
              fullWidth
            />
          </Box>
        </Collapse>

        {warningMessage && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {warningMessage}
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Box color="text.secondary">
          <Button variant="text" color="inherit" rounded onClick={onCloseClick} size="large" disabled={loading}>
            <FormattedMessage id="common.cancel" />
          </Button>
        </Box>
        <LoadingButton
          loading={loading}
          variant="contained"
          rounded
          onClick={() => onSubmit?.(reason === 'Other' ? reasonComment : reason)}
          size="large"
          disabled={!validValue}
        >
          <FormattedMessage id="common.submit" />
        </LoadingButton>
      </DialogActions>
    </>
  );
}

type ShiftInfo = {
  shiftStart?: Dayjs;
  shiftLength?: number;
  qualifications?: Qualification[];
  assigneeName?: string;
};
